// src/components/planner/PlannerPage.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { LayoutGrid, Calendar, RotateCcw } from 'lucide-react';
import OverviewTab from './tabs/OverviewTab';
import AgendaTab from './tabs/AgendaTab';
import ReviewTab from './tabs/ReviewTab';
import './styles/planner-base.css';
import './styles/planner-overview.css';
import './styles/planner-agenda.css';
import './styles/planner-review-modals.css';
import { useStudyTimer } from '../../contexts/StudyTimerContext';

const API_BASE_URL = '/api';

interface PlannerPageProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

type TabType = 'overview' | 'agenda' | 'review';

const PlannerPage: React.FC<PlannerPageProps> = ({ onShowToast }) => {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<TabType>('overview');

    const [todayData, setTodayData] = useState<any>(null);
    const [weekStats, setWeekStats] = useState<any>(null);
    const [subjects, setSubjects] = useState<any[]>([]);
    const [goals, setGoals] = useState<any[]>([]);
    const [allEvents, setAllEvents] = useState<any[]>([]);
    const [studyHistory, setStudyHistory] = useState<any[]>([]);
    const [reviewContents, setReviewContents] = useState<any[]>([]);
    const [tasks, setTasks] = useState<any[]>([]);
    const [taskGroups, setTaskGroups] = useState<any[]>([]);

    // ✅ Registrar o toast no contexto global do timer
    const { setToastFn } = useStudyTimer();
    useEffect(() => {
        setToastFn(onShowToast);
    }, [onShowToast, setToastFn]);

    // ========== CARREGAMENTO ==========

    const loadTodayData = useCallback(async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/today`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setTodayData(data.today);
            }
        } catch (error) {
            console.error('Erro ao carregar dados:', error);
        }
    }, []);

    const loadWeekStats = useCallback(async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/week-stats`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setWeekStats(data.weekStats);
            }
        } catch (error) {
            console.error('Erro ao carregar stats:', error);
        }
    }, []);

    const loadSubjects = useCallback(async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/subjects`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setSubjects(data.subjects);
            }
        } catch (error) {
            console.error('Erro ao carregar assuntos:', error);
        }
    }, []);

    const loadGoals = useCallback(async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/goals`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setGoals(data.goals);
            }
        } catch (error) {
            console.error('Erro ao carregar metas:', error);
        }
    }, []);

    const loadAllEvents = useCallback(async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/fixed-events`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setAllEvents(data.events);
            }
        } catch (error) {
            console.error('Erro ao carregar eventos:', error);
        }
    }, []);

    const loadStudyHistory = useCallback(async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/study-sessions`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setStudyHistory(data.sessions || []);
            }
        } catch (error) {
            console.error('Erro ao carregar histórico:', error);
        }
    }, []);

    const loadReviewContents = useCallback(async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/review-contents`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setReviewContents(data.contents || []);
            }
        } catch (error) {
            console.error('Erro ao carregar revisões:', error);
        }
    }, []);

    const loadTasks = useCallback(async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/tasks`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setTasks(data.tasks || []);
            }
        } catch (error) {
            console.error('Erro ao carregar tarefas:', error);
        }
    }, []);

    const loadTaskGroups = useCallback(async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/task-groups`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setTaskGroups(data.groups || []);
            }
        } catch (error) {
            console.error('Erro ao carregar grupos:', error);
        }
    }, []);

    const loadAllData = useCallback(async () => {
        setLoading(true);
        await Promise.all([
            loadTodayData(),
            loadWeekStats(),
            loadSubjects(),
            loadGoals(),
            loadAllEvents(),
            loadStudyHistory(),
            loadReviewContents(),
            loadTasks(),
            loadTaskGroups()
        ]);
        setLoading(false);
    }, [loadTodayData, loadWeekStats, loadSubjects, loadGoals, loadAllEvents, loadStudyHistory, loadReviewContents, loadTasks, loadTaskGroups]);

    useEffect(() => {
        loadAllData();
    }, [loadAllData]);

    if (loading) {
        return (
            <div className="plnr-page">
                <div className="plnr-loading">
                    <div className="plnr-spinner"></div>
                    <p>Carregando assistente GRAIA...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="plnr-page">
            <div className="plnr-container">
                <div className="plnr-tab-selector">
                    <button
                        className={`plnr-tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        <LayoutGrid size={18} />
                        Visão Geral
                    </button>
                    <button
                        className={`plnr-tab-btn ${activeTab === 'agenda' ? 'active' : ''}`}
                        onClick={() => setActiveTab('agenda')}
                    >
                        <Calendar size={18} />
                        Agenda
                    </button>
                    <button
                        className={`plnr-tab-btn ${activeTab === 'review' ? 'active' : ''}`}
                        onClick={() => setActiveTab('review')}
                    >
                        <RotateCcw size={18} />
                        Revisão
                    </button>
                </div>

                {/* ✅ activeTimer e setActiveTimer removidos — vêm do contexto dentro do OverviewTab */}
                {activeTab === 'overview' && (
                    <OverviewTab
                        todayData={todayData}
                        weekStats={weekStats}
                        goals={goals}
                        studyHistory={studyHistory}
                        reviewContents={reviewContents}
                        tasks={tasks}
                        taskGroups={taskGroups}
                        subjects={subjects}
                        onReload={loadAllData}
                        onReloadTasks={async () => { await Promise.all([loadTasks(), loadTaskGroups()]); }}
                        onShowToast={onShowToast}
                    />
                )}

                {activeTab === 'agenda' && (
                    <AgendaTab
                        allEvents={allEvents}
                        subjects={subjects}
                        onReload={loadAllData}
                        onShowToast={onShowToast}
                    />
                )}

                {activeTab === 'review' && (
                    <ReviewTab
                        reviewContents={reviewContents}
                        onReload={loadReviewContents}
                        onShowToast={onShowToast}
                    />
                )}
            </div>
        </div>
    );
};

export default PlannerPage;